import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../config";

const servicesList = [
    "Tax Filing",
    "GST",
    "Audit",
    "Accounting & Bookkeeping",
    "Company Registration",
    "Compliance & ROC",
    "Advisory / Consulting",
];

const Add_ca = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [services, setServices] = useState([]);
  const [startups, setStartups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingStartups, setLoadingStartups] = useState(true);

  const [formData, setFormData] = useState({
    startupId: "",
    caName: "",
    email: "",
    phone: "",
    registrationNumber: "",
    experience: "",
    practiceType: "",
    city: "",
    status: "Active",
  });

  useEffect(() => {
    const fetchStartups = async () => {
      try {
        setLoadingStartups(true);
        const res = await apiClient.get("/api/admin_routes/startups/all?limit=1000");
        const list = res.data.startups || [];
        setStartups(list);
        if (list.length > 0) {
          setFormData((prev) => ({ ...prev, startupId: list[0]._id }));
        }
      } catch (err) {
        console.error("Error loading startups:", err);
        toast.error("Failed to load startups list");
      } finally {
        setLoadingStartups(false);
      }
    };
    fetchStartups();
  }, []);

  const addService = (service) => {
    if (!service) return;
    if (services.includes(service)) return;

    setServices([...services, service]);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addService(input.trim());
    }

    if (e.key === "Backspace" && !input && services.length) {
      setServices(services.slice(0, -1));
    }
  };

  const removeService = (service) => {
    setServices(services.filter((s) => s !== service));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.startupId) {
      toast.error("Please select a startup first");
      return;
    }

    const billingAddress = [
      `Experience: ${formData.experience || "Not specified"}`,
      `Practice Type: ${formData.practiceType || "Not specified"}`,
      `Status: ${formData.status}`,
      services.length > 0 ? `Services: ${services.join(", ")}` : "",
    ].filter(Boolean).join(" | ");

    const payload = {
      vendorName: formData.caName.trim(),
      vendorType: "consultant",
      vendorContact: formData.caName.trim(),
      vendorEmail: formData.email.trim(),
      vendorPhone: formData.phone.trim(),
      vendorCountry: "India",
      vendorState: formData.city.trim() || "Delhi",
      vendorTaxId: formData.registrationNumber.trim() || "",
      vendorBillingAddress: billingAddress,
    };

    try {
      setSubmitting(true);
      const res = await apiClient.post(`/api/vendors/${formData.startupId}`, payload);
      if (res.data.success) {
        toast.success("Chartered Accountant added successfully!");
        navigate("/dashboard");
      } else {
        throw new Error(res.data.message || "Failed to save CA");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save CA");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header border-bottom bg-transparent py-3">
              <h5 className="mb-0 fw-semibold text-dark">Add Chartered Accountant</h5>
            </div>
            <div className="card-body p-4">
              <form className="row g-3" onSubmit={handleSubmit}>
                {/* Select Startup */}
                <div className="col-12">
                  <label className="form-label text-secondary">Link to Startup *</label>
                  {loadingStartups ? (
                    <div className="text-muted small">Loading startups...</div>
                  ) : (
                    <select
                      className="form-select"
                      name="startupId"
                      value={formData.startupId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Startup</option>
                      {startups.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.companyName || s.founderName || s.email}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">CA Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="caName"
                    value={formData.caName}
                    onChange={handleInputChange}
                    placeholder="Enter CA name"
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Phone Number *</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">CA Registration Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleInputChange}
                    placeholder="Enter ICAI registration number"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Experience</label>
                  <select
                    className="form-select"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                  >
                    <option value="">Select experience</option>
                    <option>Fresher</option>
                    <option>1–3 Years</option>
                    <option>3–7 Years</option>
                    <option>7+ Years</option>
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Practice Type</label>
                  <select
                    className="form-select"
                    name="practiceType"
                    value={formData.practiceType}
                    onChange={handleInputChange}
                  >
                    <option value="">Select practice type</option>
                    <option>Individual</option>
                    <option>Firm</option>
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">City *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                    required
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label text-secondary">Services Offered</label>

                  <div className="form-control d-flex flex-wrap gap-2 p-2">
                    {services.map((service) => (
                      <span
                        key={service}
                        className="badge bg-primary d-flex align-items-center"
                      >
                        {service}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          onClick={() => removeService(service)}
                        />
                      </span>
                    ))}

                    <input
                      type="text"
                      className="border-0 flex-grow-1"
                      placeholder="Type service and press Enter"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      list="services-suggestions"
                      style={{ outline: "none" }}
                    />
                  </div>

                  <datalist id="services-suggestions">
                    {servicesList.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>

                  <small className="text-muted">
                    Press Enter or comma to add · Backspace to remove last
                  </small>
                </div>

                <div className="col-12 d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={submitting}
                  >
                    {submitting ? "Saving..." : "Save CA"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Add_ca;
